import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import ProductsApi from "@/store/products/-api";

@Module({
  name: "products",
  stateFactory: true,
  namespaced: true,
})
export default class ProductsStore extends VuexModule {
  products: ProductMap = {};

  @Action({ rawError: true })
  async fetchProductsByIds({ ids, fetchNestedModels }: { ids: string[]; fetchNestedModels?: boolean }) {
    if (ids.length === 0) {
      return;
    }
    const api = new ProductsApi(this.store.$axios);
    const response = await api.get({ ids });

    await processCoreModelResponse(this.store, response.data, fetchNestedModels);
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchProductById(id: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.products[id])) {
      return;
    }

    try {
      const api = new ProductsApi(this.store.$axios);
      const response = await api.getOne(id);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return undefined;
      }
    }
  }

  @Mutation
  setProducts({ products }: { products: ProductMap }) {
    // Set core models first
    Vue.set(this, "products", Object.assign({}, this.products, products));
  }
}

export const getProductsStore = (store: Store<unknown>) => {
  const productsStore = getModule(ProductsStore, store);

  return productsStore;
};
